import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { DashboardAgreement, ManageAgreementsState } from './typings'
import { getAgreements, getAgreementWidgetSettingsData, postData } from './api'
import { StoreData } from '@user/store/reducers/typings'
import { updateAssigneeIdsNullValue } from '@user/components/Dashboard/widgets/utils/updateAssigneeIdsNullValue'

const agreementAdapter = createEntityAdapter<DashboardAgreement>({
  selectId: (agreement) => agreement.formId,
})

export const initialState: ManageAgreementsState = {
  count: 0,
  isLoading: false,
  items: agreementAdapter.getInitialState(),
  page: 1,
  showFront: true,
  skip: 0,
  take: 0,
  salesTeamIds: [],
  assigneeIds: [],
  numberOfDays: 0,
}

const manageAgreementsSlice = createSlice({
  name: 'dashboard/manageAgreements',
  initialState: initialState,
  reducers: {
    flipCard: (state: ManageAgreementsState) => {
      state.showFront = !state.showFront
    },
    setSalesTeamsIds: (
      state: ManageAgreementsState,
      { payload }: PayloadAction<number[]>,
    ) => {
      state.salesTeamIds = payload
    },
    setAssigneeIds: (
      state: ManageAgreementsState,
      { payload }: PayloadAction<string[]>,
    ) => {
      state.assigneeIds = payload
    },
    setPreferences: (
      state,
      { payload }: PayloadAction<ManageAgreementsState>,
    ) => {
      state.assigneeIds = payload.assigneeIds || []
      state.numberOfDays = payload.numberOfDays || 7
      state.salesTeamIds = payload.salesTeamIds || []
    },
    setPage: (state: ManageAgreementsState, action: PayloadAction<number>) => {
      state.page = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAgreements.fulfilled, (state, { payload }) => {
      agreementAdapter.upsertMany(state.items, payload.items)
      state.isLoading = false
      state.take = payload.take
      state.count = payload.count
      state.skip = payload.skip
    })
    builder.addCase(getAgreements.pending, (state) => {
      agreementAdapter.removeAll(state.items)
      state.isLoading = true
    })
    builder.addCase(
      getAgreementWidgetSettingsData.fulfilled,
      (state, { payload }) => {
        payload.assigneeIds = updateAssigneeIdsNullValue(payload.assigneeIds)
        state.salesTeamIds = payload.salesTeamIds ? payload.salesTeamIds : []
        state.assigneeIds = payload.assigneeIds ? payload.assigneeIds : []
        state.numberOfDays = payload.numberOfDays ? payload.numberOfDays : 7
      },
    )
    builder.addCase(postData.fulfilled, (state, { payload }) => {
      state.assigneeIds = updateAssigneeIdsNullValue(payload.assigneeIds)
      state.salesTeamIds = payload.salesTeamIds || []
      state.numberOfDays = payload.numberOfDays ?? 7
    })
  },
})

export const selectManageAgreements = (state: StoreData) => {
  return state.dashboard.manageAgreements
}

export const agreementSelectors = agreementAdapter.getSelectors<StoreData>(
  (state) => state.dashboard.manageAgreements.items,
)
export const { selectAll: selectAllAgreements } = agreementSelectors

export const { flipCard, setPage, setSalesTeamsIds, setAssigneeIds } =
  manageAgreementsSlice.actions
export const reducer = manageAgreementsSlice.reducer
